import { useState } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import type { CustomerRefund, DashboardCustomer } from '@models/customers';
import { useToast } from '@hooks/useToast';

import s from './ExportRefundButton.module.css';

type ExportRefundButtonProps = {
  customer: DashboardCustomer;
  refund: CustomerRefund;
};

function ExportRefundButton({ customer, refund }: ExportRefundButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const toast = useToast();

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // The PDF engine + embedded fonts live in a lazy chunk, loaded on first export.
      const [{ renderRefundPdf, getRefundPdfFilename }, { loadRefundPdfFonts }] = await Promise.all(
        [import('./refundPdf'), import('./pdfFonts')],
      );
      const fonts = await loadRefundPdfFonts();
      renderRefundPdf(customer, refund, fonts).save(getRefundPdfFilename(customer, refund));
    } catch {
      toast.error('Could not export the refund PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      type="button"
      className={s.exportBtn}
      onClick={handleExport}
      disabled={isExporting}
      title="Export refund as PDF"
      aria-label={`Export refund ${refund.numericId} as PDF`}>
      {isExporting ? (
        <span className={s.spinner} aria-hidden="true" />
      ) : (
        <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
      )}
    </button>
  );
}

export default ExportRefundButton;
